import { InitiateDatapointEnrollmentRequest } from '@weave/schema-gen-ts/dist/schemas/syncapp/support/support.pb';
import { chunk } from 'lodash-es';
import { http } from '@frontend/fetch';
import {
  AccessDataResponse,
  AddUpdateAccessRuleResponse,
  ChildrenLocationResponse,
  ClientLocationResponse,
  Contact,
  CreatePayload,
  DataSourceResponse,
  DeleteAccessRuleResponse,
  IntegrationMetadataInfo,
  ListAccessResponse,
  LocationAccessDataResponse,
  PostCSVResponse,
  ProvisionIntegrationPayload,
  SourceId,
  SyncAppAccessRuleType,
  SyncAppData,
  SyncAppResponse,
} from './types';

const accessRuleUrl = 'portal/v1/access';
const clientLocationUrl = 'portal/v1/client-location';
const dataSourcesUrl = 'portal/v1/datasource';
const listAccessUrl = 'portal/v1/listaccess';
const locationAccessUrl = 'portal/v1/location/';
const locationsAccessUrl = 'portal/v1/locations?location_id=';
const syncAppApiUrl = 'sync-app-api/v0/csv';
const syncAppBaseUrl = 'portal/v1/syncapp';
const syncHealthUrl = syncAppBaseUrl + '/health';
const integrationProvisionUrl = 'support/v1/syncapp/provision';
const enrollDatapointPractice = 'syncapp/support/datapoint/initiate-enrollment';
const integrationsMetadataUrl = `${integrationProvisionUrl}/metadata`;

export const health = () => {
  return http.getData<SyncAppData>(syncHealthUrl);
};

export const getLocationSyncHealthData = async (locationId: string): Promise<SyncAppData> =>
  await http.getData(syncHealthUrl, {
    headers: { 'Location-Id': locationId },
  });

export const createSyncApp = (name: string, locationId: string) => {
  return http.post<SyncAppResponse, CreatePayload>(
    `${syncAppBaseUrl}`,
    {
      Name: name,
      Type: 'CSV',
    },
    {
      headers: { 'Location-Id': locationId },
    }
  );
};

export const updateDataSourceName = async ({
  sourceId,
  dataSourceName,
  locationId,
}: SourceId & {
  dataSourceName: string;
  locationId: string;
}) =>
  http.put<unknown>(
    `${syncAppBaseUrl}/${sourceId}`,
    { Name: dataSourceName },
    { headers: { 'Location-Id': locationId } }
  );

export const postNewSyncApp = async ({ newCsvName }: { newCsvName: string }) =>
  await http.post<SyncAppResponse>(syncAppBaseUrl, {
    Name: newCsvName,
    Type: 'CSV',
  });

export const deleteSyncApp = async ({ sourceId, locationId }: SourceId & { locationId: string }) => {
  await purgeCsvDataSource(sourceId, locationId);
  await deleteCsvDataSource(sourceId, locationId);
};

export const revokeIntegrationAuthentication = async ({ sourceId }: SourceId) =>
  await http.post(`${syncAppBaseUrl}/access/revoke?sourceID=${sourceId}`);

const chunkSize = 500;
export const uploadCsvData = async (contacts: Contact[], dataSourceId: string, locationId: string) => {
  const chunks: Contact[][] = chunk(contacts, chunkSize);
  const response = await Promise.all(chunks.map((chunk) => updateCsvDataSource(chunk, dataSourceId, locationId)));
  return response.reduce<PostCSVResponse>(
    (acc, res) => {
      acc.data.BadRecords = res.data.BadRecords
        ? acc.data.BadRecords!.concat(res.data.BadRecords)
        : acc.data.BadRecords;
      acc.data.ContactInfosImported += res.data.ContactInfosImported;
      acc.data.successful += res.data.successful;
      acc.data.total += res.data.total;
      acc.records_updated += res.records_updated;

      return acc;
    },
    { data: { BadRecords: [], ContactInfosImported: 0, successful: 0, total: 0 }, records_updated: 0 }
  );
};

export const updateCsvDataSource = (persons: Contact[], dataSourceId: string, locationId: string) =>
  http.post<PostCSVResponse, { persons: Contact[] }>(
    `${syncAppApiUrl}/${dataSourceId}/persons`,
    { persons },
    {
      headers: { 'Location-Id': locationId },
    }
  );

export const purgeCsvDataSource = (sourceId: string, locationId: string) =>
  http.delete(`${syncAppApiUrl}/${sourceId}/persons`, {
    headers: { 'Location-Id': locationId },
  });

export const deleteCsvDataSource = (sourceId: string, locationId: string) =>
  http.delete(`${syncAppApiUrl}/${sourceId}`, {
    headers: { 'Location-Id': locationId },
  });

export const updateCsvData = async (persons: Contact[], dataSourceId: string, locationId: string) => {
  await purgeCsvDataSource(dataSourceId, locationId);
  return uploadCsvData(persons, dataSourceId, locationId);
};

export const createCsvSyncApp = async (name: string, persons: Contact[], locationId: string) => {
  const response = await createSyncApp(name, locationId);
  return uploadCsvData(persons, response.data, locationId);
};

export const getDesktopLocationAccessData = (locationId: string) => {
  return http.getData<AccessDataResponse>(`desktop/v1/location/${locationId}/access`, {
    headers: { 'Location-Id': locationId },
  });
};

export const getLocationsAccessData = (locationId: string): Promise<LocationAccessDataResponse[]> =>
  http.getData(locationsAccessUrl + locationId, {
    headers: { 'Location-Id': locationId },
  });

export const getAccessData = (locationId: string): Promise<AccessDataResponse> =>
  http.getData(locationAccessUrl + locationId + '/access', {
    headers: { 'Location-Id': locationId },
  });

export const getClientLocation = async (groupId: string): Promise<ClientLocationResponse[]> =>
  await http.getData(clientLocationUrl, {
    headers: { 'Location-Id': groupId },
  });

export const getDataSource = async (groupId: string): Promise<DataSourceResponse> =>
  await http.getData(dataSourcesUrl, {
    headers: { 'Location-Id': groupId },
  });

export const getListAccess = async (locationId: string): Promise<ListAccessResponse[]> =>
  await http.getData(listAccessUrl, {
    headers: { 'Location-Id': locationId },
  });

export const getListAccessByLocation = async (locationId: string): Promise<ListAccessResponse[]> =>
  await http.getData(listAccessUrl, { headers: { 'Location-Id': locationId } });

export const getChildrenLocation = async (locationId: string): Promise<ChildrenLocationResponse[]> =>
  await http.getData(`portal/v1/location/${locationId}/children`, {
    headers: { 'Location-Id': locationId },
  });

export const postAccessRule = async (
  accessRule: SyncAppAccessRuleType,
  locationId: string
): Promise<AddUpdateAccessRuleResponse> =>
  await http.post(accessRuleUrl, accessRule, {
    headers: { 'Location-Id': locationId },
  });

export const updateAccessRule = async (
  accessRule: SyncAppAccessRuleType,
  locationId: string
): Promise<AddUpdateAccessRuleResponse> =>
  await http.put(`${accessRuleUrl}/${accessRule.LocationID}`, accessRule, {
    headers: { 'Location-Id': locationId },
  });

export const deleteAccessRule = async (
  accessRule: SyncAppAccessRuleType,
  locationId: string
): Promise<DeleteAccessRuleResponse> =>
  await http.delete(`${accessRuleUrl}/${accessRule.LocationID}`, accessRule, {
    headers: { 'Location-Id': locationId },
  });

export const getAccessibleIntegrations = async (childOrSingleLocationId: string): Promise<SyncAppData> => {
  const config = childOrSingleLocationId ? { headers: { 'Location-Id': childOrSingleLocationId } } : undefined;
  return (await http.getData<SyncAppData>('portal/v1/syncapp/health', config)) || [];
};

export const getIntegrationsMetadata = async (locationId: string) => {
  const response = await http.getData<{ integrations: IntegrationMetadataInfo[] }>(integrationsMetadataUrl, {
    headers: { 'Location-Id': locationId },
  });
  return response.integrations ?? [];
};

export const provisionIntegration = async (payload: ProvisionIntegrationPayload, locationId: string) => {
  return http.post<unknown>(integrationProvisionUrl, payload, {
    headers: { 'Location-Id': locationId },
  });
};

export const initiateDatapointPracticeEnrollment = async (
  payload: InitiateDatapointEnrollmentRequest,
  locationId: string
) => {
  return http.post<unknown>(enrollDatapointPractice, payload, {
    headers: { 'Location-Id': locationId },
  });
};
